import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTE_NAMES } from '@tsgecart/config';
import { AdminPageHeader } from '../../layouts/admin-page-header.js';
import { AdminContent } from '../../layouts/admin-content.js';
import { AdminSectionCard } from '../../components/primitives/admin-section-card.js';
import { AdminLoadingState } from '../../components/primitives/admin-loading-state.js';
import { AdminErrorState } from '../../components/primitives/admin-error-state.js';
import { AdminApiError } from '../../api/admin-client.js';
import { useAdminProduct, useAdminProductMutations } from '../hooks/use-products.js';
import { useAdminCategories } from '../hooks/use-categories.js';
import { useAdminBrands } from '../hooks/use-brands.js';
import { useAdminTaxRates } from '../hooks/use-tax-rates.js';
import { ProductGeneralTab } from '../components/product-general-tab.js';
import { ProductSeoTab } from '../components/product-seo-tab.js';
import { ProductStatusTab } from '../components/product-status-tab.js';
import { ProductVariantsTab } from '../components/product-variants-tab.js';
import { ProductMediaTab } from '../components/product-media-tab.js';

export interface ProductFormState {
  name: string;
  slug: string;
  categoryId: string;
  brandId: string;
  taxRateId: string;
  description: string;
  metaTitle: string;
  metaDescription: string;
  canonicalUrl: string;
  ogImageMediaAssetId: string;
  isFeatured: boolean;
}

const EMPTY_FORM: ProductFormState = {
  name: '',
  slug: '',
  categoryId: '',
  brandId: '',
  taxRateId: '',
  description: '',
  metaTitle: '',
  metaDescription: '',
  canonicalUrl: '',
  ogImageMediaAssetId: '',
  isFeatured: false,
};

const TABS = ['General', 'Variants', 'Media', 'SEO', 'Status'] as const;
type Tab = (typeof TABS)[number];

export function ProductEditorPage() {
  const { productId } = useParams<{ productId: string }>();
  const isEditMode = Boolean(productId);
  const navigate = useNavigate();

  const { data: product, isLoading, isError, refetch } = useAdminProduct(productId);
  const { data: categories } = useAdminCategories();
  const { data: brands } = useAdminBrands();
  const { data: taxRates } = useAdminTaxRates();
  const { create, update, archive, activate } = useAdminProductMutations();

  const [tab, setTab] = useState<Tab>('General');
  const [form, setForm] = useState<ProductFormState>(EMPTY_FORM);
  const [savedSnapshot, setSavedSnapshot] = useState<string>(JSON.stringify(EMPTY_FORM));
  const [errors, setErrors] = useState<Partial<Record<keyof ProductFormState, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!product) return;
    const next: ProductFormState = {
      name: product.name,
      slug: product.slug,
      categoryId: product.categoryId,
      brandId: product.brandId ?? '',
      taxRateId: product.taxRateId,
      description: product.description ?? '',
      metaTitle: product.metaTitle ?? '',
      metaDescription: product.metaDescription ?? '',
      canonicalUrl: product.canonicalUrl ?? '',
      ogImageMediaAssetId: product.ogImageMediaAssetId ?? '',
      isFeatured: product.isFeatured,
    };
    setForm(next);
    setSavedSnapshot(JSON.stringify(next));
  }, [product]);

  const isDirty = JSON.stringify(form) !== savedSnapshot;

  // Warns on tab close/refresh only -- react-router v6 in this app uses
  // <BrowserRouter>/<Routes> (not a data router), so `useBlocker` for
  // in-app navigation isn't available here; documented as a known gap
  // rather than pulling in a data-router migration for this milestone.
  useEffect(() => {
    if (!isDirty) return;
    function handler(event: BeforeUnloadEvent) {
      event.preventDefault();
    }
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  function patchForm(patch: Partial<ProductFormState>) {
    setForm((prev) => ({ ...prev, ...patch }));
    setSuccessMessage(null);
  }

  function validate(): boolean {
    const nextErrors: Partial<Record<keyof ProductFormState, string>> = {};
    if (!form.name.trim()) nextErrors.name = 'Name is required.';
    if (!form.categoryId) nextErrors.categoryId = 'Choose a category.';
    if (!form.taxRateId) nextErrors.taxRateId = 'Choose a tax rate.';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  function buildPayload() {
    return {
      name: form.name.trim(),
      slug: form.slug.trim() || undefined,
      categoryId: form.categoryId,
      brandId: form.brandId || null,
      taxRateId: form.taxRateId,
      description: form.description.trim() || null,
      metaTitle: form.metaTitle.trim() || null,
      metaDescription: form.metaDescription.trim() || null,
      canonicalUrl: form.canonicalUrl.trim() || null,
      ogImageMediaAssetId: form.ogImageMediaAssetId || null,
      isFeatured: form.isFeatured,
    };
  }

  function handleSubmit() {
    setSubmitError(null);
    if (!validate()) return;

    if (isEditMode && productId) {
      update.mutate(
        { id: productId, input: buildPayload() },
        {
          onSuccess: () => {
            setSavedSnapshot(JSON.stringify(form));
            setSuccessMessage('Saved.');
          },
          onError: (error) => setSubmitError(error instanceof AdminApiError ? error.message : 'Could not save.'),
        },
      );
    } else {
      create.mutate(buildPayload(), {
        onSuccess: (created) => {
          setSuccessMessage('Product created.');
          navigate(`${ROUTE_NAMES.adminProducts}/${created.id}`, { replace: true });
        },
        onError: (error) => setSubmitError(error instanceof AdminApiError ? error.message : 'Could not create.'),
      });
    }
  }

  if (isEditMode && isLoading) {
    return (
      <AdminContent>
        <AdminLoadingState label="Loading product" />
      </AdminContent>
    );
  }
  if (isEditMode && isError) {
    return (
      <AdminContent>
        <AdminErrorState message="Product could not be loaded." onRetry={() => void refetch()} />
      </AdminContent>
    );
  }

  const isSaving = create.isPending || update.isPending;

  return (
    <AdminContent>
      <AdminPageHeader
        title={isEditMode ? form.name || 'Edit product' : 'New product'}
        breadcrumbs={[{ label: 'Products', to: ROUTE_NAMES.adminProducts }, { label: isEditMode ? 'Edit' : 'New' }]}
        actions={
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSaving || (isEditMode && !isDirty)}
            className="bg-brand text-brand-foreground rounded-full px-5 py-2 text-sm font-semibold disabled:opacity-60"
          >
            {isSaving ? 'Saving…' : isEditMode ? 'Save changes' : 'Create product'}
          </button>
        }
      />

      {isDirty && isEditMode && (
        <p className="mb-3 text-sm text-amber-700" role="status">
          You have unsaved changes.
        </p>
      )}
      {successMessage && (
        <p className="mb-3 text-sm text-green-700" role="status">
          {successMessage}
        </p>
      )}
      {submitError && (
        <p className="mb-3 text-sm text-red-600" role="alert">
          {submitError}
        </p>
      )}

      <div className="mb-4 flex gap-1 border-b border-gray-100" role="tablist" aria-label="Product sections">
        {TABS.map((t) => {
          const disabled = !isEditMode && t !== 'General' && t !== 'SEO';
          return (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              disabled={disabled}
              title={disabled ? 'Available after the product is created' : undefined}
              onClick={() => setTab(t)}
              className={`focus-visible:ring-brand -mb-px border-b-2 px-3 py-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:text-gray-300 ${
                tab === t ? 'border-brand text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t}
            </button>
          );
        })}
      </div>

      <AdminSectionCard title={tab} headingLevel="h2">
        {tab === 'General' && (
          <ProductGeneralTab
            form={form}
            onChange={patchForm}
            categories={categories ?? []}
            brands={brands ?? []}
            taxRates={taxRates ?? []}
            errors={errors}
          />
        )}
        {tab === 'Variants' && productId && <ProductVariantsTab productId={productId} />}
        {tab === 'Media' && productId && <ProductMediaTab productId={productId} />}
        {tab === 'SEO' && <ProductSeoTab form={form} onChange={patchForm} />}
        {tab === 'Status' && product && (
          <ProductStatusTab
            status={product.status}
            publishedAt={product.publishedAt}
            isBusy={archive.isPending || activate.isPending}
            onArchive={() => productId && archive.mutate(productId)}
            onActivate={() => productId && activate.mutate(productId)}
          />
        )}
      </AdminSectionCard>
    </AdminContent>
  );
}
